/**
 * ToggleButton component for toggling states.
 * Simple and modern bouton on/off, rounded, with switch, green when active.
 * @param onClick - Callback function to execute on button click.
 * @returns The ToggleButton component.
 */

import React from 'react';
import * as Switch from '@radix-ui/react-switch';
import { Skeleton } from '@radix-ui/themes';
import '../../styles/global.css';

interface ToggleProps {
	isOn: boolean;
	onChange?: (value: boolean) => void;
	id?: string;
	size?: 'sm' | 'md' | 'lg';
	loading?: boolean;
	readonly?: boolean;
}

const ToggleButton: React.FC<ToggleProps> = ({
	isOn,
	onChange,
	id,
	size,
	loading,
	readonly = false,
}) => {
	const switchId = id || `toggle-${Math.random().toString(36).slice(2, 11)}`;
	return (
		<div
			className={`inline-flex items-center ${size === 'sm' ? 'h-5' : size === 'lg' ? 'h-8' : 'h-6'}`}
		>
			{loading ? (
				<Skeleton
					className={`
						relative inline-flex items-center rounded-full
						${size === 'sm' ? 'h-5 w-9' : size === 'lg' ? 'h-8 w-14' : 'h-6 w-11'}
						bg-gradient-to-l from-gray-200 via-gray-100 to-gray-200
						`}
					style={{
						backgroundSize: '200% 100%',
						animation: 'shimmer 1.5s infinite linear',
					}}
				/>
			) : (
				<Switch.Root
					id={switchId}
					checked={isOn}
					onCheckedChange={onChange}
					className={`relative inline-flex items-center rounded-full transition-colors ${
						isOn ? 'bg-green-500' : 'bg-gray-300'
					} ${size === 'sm' ? 'h-5 w-9' : size === 'lg' ? 'h-8 w-14' : 'h-6 w-11'}
					${readonly ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
			`}
				>
					<Switch.Thumb
						className={
							`block bg-white rounded-full transform transition-transform ` +
							(size === 'sm'
								? `${isOn ? 'h-3 w-3 translate-x-5' : 'h-3 w-3 translate-x-1'}`
								: size === 'lg'
									? `${isOn ? 'h-6 w-6 translate-x-7' : 'h-6 w-6 translate-x-1'}`
									: `${isOn ? 'h-4 w-4 translate-x-6' : 'h-4 w-4 translate-x-1'}`)
						}
					/>
				</Switch.Root>
			)}
		</div>
	);
};

export default ToggleButton;
